import React, { memo, useState, useCallback, useContext, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { createUseStyles, Icon, Text, Tooltip } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import Content from './Content';
import { FiltersContext } from './Context';
import { operators } from './operators';
import useAddModal from './useAddModal';
import useFilters from './hooks/useFilters';

import styles from './styles';

const {
    EMPTY,
    NOT_EMPTY,
    EQUALS,
    NOT_EQUALS,
    GREATER,
    GREATER_OR_EQUAL,
    LESS,
    LESS_OR_EQUAL,
    IN,
    NOT_IN,
    LIKE,
    NOT_LIKE,
    TODAY,
    YESTERDAY,
    TOMORROW,
    THIS_WEEK,
    LAST_WEEK,
    NEXT_WEEK,
    THIS_MONTH,
    LAST_MONTH,
    NEXT_MONTH,
    THIS_QUARTER,
    LAST_QUARTER,
    NEXT_QUARTER,
    THIS_YEAR,
    LAST_YEAR,
    NEXT_YEAR,
    INSIDE_RANGE,
} = operators;

const useStyles = createUseStyles(styles, 'Filters');

const ContentWrapper = memo(
    ({
        className,
        onClose,
        labels = {},
        action,
        onClearAll,
        onSaveView,
        onDeleteView,
        onDeleteCustomView,
        externalToggleViewsModal,
        externalIsOpenViewsModal,
        values,
        selectedView,
        customView,
        defaultFilters,
        schema,
        extraSchema,
        visibleFilters,
        useViewsInFilters,
        useViewsInMenu,
        onConfirmAdd,
        onDelete,
        onDeleteMultiple,
        canManagePublicViews,
        canManagePrivateViews,
        isFullWidth,
        onResetVisibles,
        crossSchema,
        crossOperators,
        extraMultiSchema,
        entity,
        selectorsSchema,
        useHeader = true,
        operators,
        forceSelectAllTypes,
        readOnlyFilterIds = [],
        ...props
    }) => {
        const classes = useStyles();
        const classNames = classnames(classes.root, className, {
            [classes.isFullWidth]: isFullWidth,
        });
        const [isOpenViewsModal, setIsOpenViewsModal] = useState(false);
        const { t } = useWeb5Context();
        const { clearFilters, setClearFilters, setWithExtraFields, withActiveAdvancedFilters } =
            useContext(FiltersContext);

        const { hasFiltersApplied } = useFilters({ crossOperators, operators, values });

        const modalOptionsProps = useAddModal({
            entity,
            defaultFilters,
            extraSchema,
            extraMultiSchema,
            onConfirmAdd,
            onDeleteMultiple,
            onResetVisibles,
            visibleFilters,
            selectorsSchema,
            labels,
            onDelete,
            forceSelectAllTypes,
            readOnlyFilterIds,
        });

        const clearFiltersClassName = useMemo(() => {
            if (!hasFiltersApplied && !withActiveAdvancedFilters)
                return classes.actionsClearDisabled;
            return classes.actionsClear;
        }, [
            hasFiltersApplied,
            withActiveAdvancedFilters,
            classes.actionsClearDisabled,
            classes.actionsClear,
        ]);

        useEffect(() => {
            const state =
                (defaultFilters && defaultFilters.length < visibleFilters.length) ||
                (values && Object.keys(values).length > 0);
            setWithExtraFields(state);
        }, [defaultFilters, visibleFilters, values, setWithExtraFields]);

        const canCreateView = useMemo(() => {
            if (!hasFiltersApplied || selectedView || customView) return false;
            return true;
        }, [hasFiltersApplied, selectedView, customView]);

        const createViewClassName = useMemo(() => {
            if (!canCreateView) return classes.actionsCreateViewDisabled;
            return classes.actionsCreateView;
        }, [canCreateView, classes]);

        const onToggleSaveViewsModal = useCallback(() => {
            if (externalToggleViewsModal) {
                externalToggleViewsModal(!externalIsOpenViewsModal || false);
                return;
            }
            setIsOpenViewsModal(!isOpenViewsModal);
        }, [isOpenViewsModal, externalToggleViewsModal, externalIsOpenViewsModal]);

        const handleClearAll = useCallback(() => {
            if (hasFiltersApplied || selectedView || clearFilters || withActiveAdvancedFilters) {
                onClearAll && onClearAll();
                setClearFilters(false);
            }
            return null;
        }, [
            hasFiltersApplied,
            selectedView,
            clearFilters,
            onClearAll,
            setClearFilters,
            withActiveAdvancedFilters,
        ]);

        useEffect(() => {
            if (clearFilters) {
                setClearFilters(false);
            }
        }, [clearFilters, handleClearAll, setClearFilters]);

        const handleToggleSaveViewModal = useCallback(() => {
            if (!canCreateView) return null;
            onToggleSaveViewsModal();
        }, [canCreateView, onToggleSaveViewsModal]);

        const handleSaveView = useCallback(
            (params) => {
                if ((useViewsInFilters || useViewsInMenu) && onSaveView) {
                    return onSaveView(params);
                }
                return null;
            },
            [onSaveView, useViewsInFilters, useViewsInMenu],
        );

        const handleDeleteView = useCallback(
            (view) => {
                if ((useViewsInFilters || useViewsInMenu) && onDeleteView) {
                    return onDeleteView(view);
                }
                return null;
            },
            [onDeleteView, useViewsInFilters, useViewsInMenu],
        );

        const handleDeleteCustomview = useCallback(
            (view) => {
                if ((useViewsInFilters || useViewsInMenu) && onDeleteCustomView) {
                    return onDeleteCustomView(view);
                }
                return null;
            },
            [onDeleteCustomView, useViewsInFilters, useViewsInMenu],
        );

        const contentProps = useMemo(
            () => ({
                canManagePrivateViews,
                canManagePublicViews,
                crossSchema,
                crossOperators,
                customView,
                defaultFilters,
                entity,
                extraMultiSchema,
                extraSchema,
                isOpenViewsModal,
                labels,
                onDelete: modalOptionsProps.handleDeleteFilter,
                onDeleteCustomView: handleDeleteCustomview,
                onDeleteView: handleDeleteView,
                onSaveView: handleSaveView,
                onToggleAddModal: modalOptionsProps.onToggleModal,
                onToggleSaveViewsModal: handleToggleSaveViewModal,
                operators,
                schema,
                selectedView,
                useViewsInFilters,
                useViewsInMenu,
                values,
                visibleFilters,
                selectorsSchema,
                modalOptionsProps,
                useHeader,
                ...props,
            }),
            [
                canManagePrivateViews,
                canManagePublicViews,
                crossSchema,
                crossOperators,
                customView,
                defaultFilters,
                entity,
                extraMultiSchema,
                extraSchema,
                isOpenViewsModal,
                labels,
                handleDeleteCustomview,
                handleDeleteView,
                handleSaveView,
                handleToggleSaveViewModal,
                operators,
                schema,
                selectedView,
                useViewsInFilters,
                useViewsInMenu,
                values,
                visibleFilters,
                selectorsSchema,
                modalOptionsProps,
                useHeader,
                props,
            ],
        );

        return (
            <div className={classNames}>
                {useHeader && (
                    <div className={classes.actions}>
                        <div className={classes.actionsMain}>
                            <div className={classes.actionsLeft}>
                                <Icon
                                    className={classes.actionsClose}
                                    name="close"
                                    size="large"
                                    onClick={onClose}
                                />
                                <Text className={classes.actionsTitle} type="h4">
                                    {labels?.titleFilters || t('label_filters')}
                                </Text>
                            </div>
                            {onClearAll && (
                                <div
                                    className={classes.actionsRight}
                                    data-cy="action_clear_filters"
                                >
                                    <Tooltip
                                        placement="left"
                                        content={
                                            labels?.clearAllTooltip || t('action_clear_filters')
                                        }
                                    >
                                        <Text
                                            className={clearFiltersClassName}
                                            type="button"
                                            onClick={handleClearAll}
                                        >
                                            {labels?.clearAll || t('action_clear')}
                                        </Text>
                                    </Tooltip>
                                </div>
                            )}
                        </div>
                        {onSaveView && (canManagePublicViews || canManagePrivateViews) && (
                            <div className={classes.actionsSub}>
                                <Text
                                    className={createViewClassName}
                                    type="caption"
                                    onClick={handleToggleSaveViewModal}
                                >
                                    {labels?.saveCurrentFiltersAsView || t('label_create_new_view')}
                                </Text>
                            </div>
                        )}
                    </div>
                )}
                <Content {...contentProps} />
            </div>
        );
    },
);

const operatorShape = PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOf([
        EMPTY,
        NOT_EMPTY,
        EQUALS,
        NOT_EQUALS,
        GREATER,
        GREATER_OR_EQUAL,
        LESS,
        LESS_OR_EQUAL,
        IN,
        NOT_IN,
        LIKE,
        NOT_LIKE,
        TODAY,
        YESTERDAY,
        TOMORROW,
        THIS_WEEK,
        LAST_WEEK,
        NEXT_WEEK,
        THIS_MONTH,
        LAST_MONTH,
        NEXT_MONTH,
        THIS_QUARTER,
        LAST_QUARTER,
        NEXT_QUARTER,
        THIS_YEAR,
        LAST_YEAR,
        NEXT_YEAR,
        INSIDE_RANGE,
    ]),
    extra: PropTypes.bool,
});

ContentWrapper.propTypes = {
    className: PropTypes.string,
    title: PropTypes.string,
    onClose: PropTypes.func,
    action: PropTypes.shape({
        title: PropTypes.string,
        onClick: PropTypes.func,
    }),
    schema: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            label: PropTypes.string,
            isRemovable: PropTypes.bool,
            fields: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string,
                    dataType: PropTypes.oneOf([
                        'text',
                        'integer',
                        'decimal',
                        'currency',
                        'percent',
                        'date',
                        'bool',
                        'probability',
                        'singleValueList',
                        'multipleValueList',
                        'relatedValueList',
                    ]),
                    inputProps: PropTypes.shape({
                        isMulti: PropTypes.bool,
                        isFuzzy: PropTypes.bool,
                        loadOptions: PropTypes.func,
                        defaultOptions: PropTypes.bool,
                        placeholder: PropTypes.string,
                        options: PropTypes.arrayOf(
                            PropTypes.shape({
                                label: PropTypes.string,
                                value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                            }),
                        ),
                        label: PropTypes.string,
                    }),
                    operator: operatorShape,
                }),
            ),
        }),
    ),
    extraSchema: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            label: PropTypes.string,
            options: PropTypes.arrayOf(
                PropTypes.shape({
                    label: PropTypes.string,
                    value: PropTypes.string,
                }),
            ),
        }),
    ),
    showAdd: PropTypes.bool,
    visibleFilters: PropTypes.array,
    onChange: PropTypes.func,
    onToggle: PropTypes.func,
    onAdd: PropTypes.func,
    onDelete: PropTypes.func,
    onClearAll: PropTypes.func,
    values: PropTypes.object,
    useViewsInFilters: PropTypes.bool,
    useViewsInMenu: PropTypes.bool,
    views: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
    ),
    canManagePublicViews: PropTypes.bool,
    canManagePrivateViews: PropTypes.bool,
    customView: PropTypes.object,
    onChangeView: PropTypes.func,
    onChangeCustomView: PropTypes.func,
    onSaveView: PropTypes.func,
    onDeleteView: PropTypes.func,
    onDeleteCustomView: PropTypes.func,
    labels: PropTypes.shape({
        titleFilters: PropTypes.string,
        clearAll: PropTypes.string,
        saveCurrentFiltersAsView: PropTypes.string,
    }),
    labelsViews: PropTypes.shape({
        views: PropTypes.string,
        viewsPlaceholder: PropTypes.string,
        viewsNoOptionsPlaceholder: PropTypes.string,
        viewsLoadingPlaceholder: PropTypes.string,
    }),
    labelsSaveViewsModal: PropTypes.shape({
        title: PropTypes.string,
        confirmText: PropTypes.string,
        cancelText: PropTypes.string,
        viewName: PropTypes.string,
        saveAs: PropTypes.string,
        viewNamePlaceholder: PropTypes.string,
        saveAsPlaceholder: PropTypes.string,
    }),
    labelsDeleteViewsModal: PropTypes.shape({
        title: PropTypes.string,
        confirmText: PropTypes.string,
        cancelText: PropTypes.string,
        questionText: PropTypes.string,
        adviceText: PropTypes.string,
        confirmWriteText: PropTypes.string,
        confirmWord: PropTypes.string,
        confirmWritePlaceholder: PropTypes.string,
    }),
    isFullWidth: PropTypes.bool,
    useHeader: PropTypes.bool,
    collapsibleSections: PropTypes.bool,
    advancedFilters: PropTypes.bool,
    operators: PropTypes.objectOf(operatorShape),
    onChangeOperator: PropTypes.func,
};

export default ContentWrapper;
